import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Renew.css";

const PoliceReturnForm = () => {
  const navigate = useNavigate();

  const [mmyy, setMmyy] = useState("");
  const [receiptNo, setReceiptNo] = useState("");
  const [transaction, setTransaction] = useState(null);
  const [error, setError] = useState("");
  const [confirmClaimed, setConfirmClaimed] = useState(null);
  const [reconfirmClaimed, setReconfirmClaimed] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const mmyyRef = useRef(null);
  const receiptRef = useRef(null);

  // Autofocus MMYY on mount
  useEffect(() => {
    requestAnimationFrame(() => {
      mmyyRef.current?.focus();
      mmyyRef.current?.select?.();
    });
  }, []);

  const onMmyyKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      receiptRef.current?.focus();
      receiptRef.current?.select?.();
    }
  };

  const onReceiptKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (mmyy && receiptNo) {
        handleSearch();
      }
    }
  };

  const handleSearch = async () => {
    try {
      const response = await axios.get(
        "https://www.lianfoong.com/api/pawn-transactions/search",
        {
          params: { mmyy, receiptNo },
        }
      );
      setTransaction(response.data);
      setError("");
      setConfirmClaimed(null);
      setReconfirmClaimed(null);
    } catch (err) {
      console.error(err);
      setError("Transaction not found!");
      setTransaction(null);
      setConfirmClaimed(null);
      setReconfirmClaimed(null);
    }
  };

  const handleInitialConfirm = (choice) => {
    if (choice === "yes") {
      setConfirmClaimed(true);
    } else {
      setConfirmClaimed(false);
    }
  };

  const handleReConfirm = (choice) => {
    if (choice === "yes") {
      setReconfirmClaimed(true);
    } else {
      setReconfirmClaimed(false);
    }
  };

  const handleFinalSubmit = async () => {
    if (!transaction?.receiptNo) {
      alert("No ticket selected.");
      return;
    }

    try {
      setSubmitting(true);

      await axios.put(
        "https://www.lianfoong.com/api/police-size-details/claim",
        null,
        {
          params: { receiptNo: transaction.receiptNo },
        }
      );

      alert("Insurance claim recorded successfully.");
      navigate("/dashboard");
    } catch (error) {
      console.error("Claim update failed:", error);
      alert(
        error?.response?.data || "Failed to mark this police seized item as claimed."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateDDMMYYYY = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="renew-container">
      <h2 className="title">Police Return / Insurance Claim</h2>

      <div className="form-group">
        <label>Enter Month and Year (MMYY):</label>
        <input
          type="text"
          ref={mmyyRef}
          value={mmyy}
          maxLength={4}
          onChange={(e) => setMmyy(e.target.value.replace(/\D/g, "").slice(0, 4))}
          onKeyDown={onMmyyKeyDown}
        />
      </div>

      <div className="form-group">
        <label>Enter Receipt Number:</label>
        <input
          type="text"
          ref={receiptRef}
          value={receiptNo}
          onChange={(e) =>
            setReceiptNo(e.target.value.replace(/\D/g, "").slice(0, 5))
          }
          onKeyDown={onReceiptKeyDown}
        />
      </div>

      <button className="search-btn" onClick={handleSearch} disabled={!mmyy || !receiptNo}>
        Search
      </button>

      {error && <p className="error-message">{error}</p>}

      {transaction && (
        <div className="transaction-details">
          <div className="details-grid">
            <p><strong>Receipt No:</strong> {transaction.receiptNo}</p>
            <p><strong>Items:</strong> {transaction.item}</p>
            <p><strong>Weight:</strong> {transaction.weight}</p>
            <p><strong>Amount:</strong> ${Number(transaction.amount || 0).toFixed(2)}</p>
            <p><strong>Date:</strong> {formatDateDDMMYYYY(transaction.grantDate)}</p>
            <p><strong>Expiry Date:</strong> {formatDateDDMMYYYY(transaction.expiryDate)}</p>
          </div>

          {confirmClaimed === null && (
            <div className="alert-box">
              <p><strong>Please confirm this police seized item has been claimed from insurance:</strong></p>
              <button onClick={() => handleInitialConfirm("yes")}>Yes</button>
              <button onClick={() => handleInitialConfirm("no")}>No</button>
            </div>
          )}

          {confirmClaimed === false && (
            <div className="alert-box">
              <p><strong>No changes made.</strong></p>
            </div>
          )}

          {confirmClaimed === true && reconfirmClaimed === null && (
            <div className="alert-box">
              <p><strong>Please re-confirm:</strong></p>
              <button onClick={() => handleReConfirm("yes")}>Yes</button>
              <button onClick={() => handleReConfirm("no")}>No</button>
            </div>
          )}

          {reconfirmClaimed === false && (
            <div className="alert-box">
              <p><strong>No changes made.</strong></p>
            </div>
          )}

          {confirmClaimed === true && reconfirmClaimed === true && (
            <div className="form-group">
              <button
                className="next-btn"
                onClick={handleFinalSubmit}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          )}
        </div>
      )}

      <button
        className="back-btn"
        type="button"
        onClick={() => navigate("/other")}
        style={{ marginTop: "16px" }}
      >
        Back
      </button>
    </div>
  );
};

export default PoliceReturnForm;